import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import FetchData, { GetOne } from "../../providers/dataProvider";
import "./FactoryOrder.scss";
import { FormatValueDecimal, CurrencyConvertion, formatDate } from "../../utils/Validate";
import moment from "moment";
import ViewOrderSummary from "./ViewOrderSummary";
import ViewAccountSummary from "./ViewAccountSummary";
import ViewOrderCharges from "./ViewOrderCharges";
import ViewDocuments from "./ViewDocuments";
import { deliveryLeadTimeValues } from "../../components/common/DeliveryLeadTime";

const ViewOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderDt, setOrderDt] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [viewOrderData, setViewOrderData] = useState(null);
  const [transportData, setTransportData] = useState([]);
  const [currencyDetail, setCurrencyDetail] = useState([]);
  const [billingTo, setBillingTo] = useState([]);
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [deliveryLeadTime, setDeliveryLeadTime] = useState([]);
  const [paymentTerm, setPaymentTerms] = useState([]);
  const [quoteGeneral, setQuoteGeneral] = useState(null);
  const [percentageValue, setPercentageValue] = useState({ customDutyValue: 0, clearnceValue: 0, taxValue: 0 })
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const { data: currencyList } = FetchData("currency");
  const { data: transportModeList } = FetchData("deliverymodes");
  const { data: deliveryTermsList } = FetchData("deliveryterms");
  const { data: paymentTermsList } = FetchData("paymentterms");
  const { data: customerData } = FetchData("Customer");
  const { data: projectData } = FetchData("project");
  const { data: distributorData } = FetchData("distributor");
  const { data: countryData } = FetchData("country");
  const { data: assignedEngineerList } = FetchData("userprofile");


  useEffect(() => {
    if (location?.state?.item?.id) {
      const item = location?.state?.item;
      setViewOrderData({
        projectDetails: location?.state?.projectDetails,
        distributorDetails: location?.state?.distributorDetails,
        countryDetails: location?.state?.countryDetails,
        customerDetails: location?.state?.customerDetails,
        currencyDetails: location?.state?.currencyDetails,
        assignedEngineerDetails: location?.state?.assignedEngineerDetails,
      })
      GetOne("order", { id: item?.customerOrderId || item?.orderId || item?.id }, setOrderDt);
    } else {
      navigate("/factory-order-list");
    }
  }, [location?.state]);

  useEffect(() => {
    if (orderDt?.message === "success")
      setViewItem(orderDt?.data);
  }, [orderDt]);

  useEffect(() => {
    if (!viewItem) return;

    const asArray = (data) => Array.isArray(data?.payload)
      ? data.payload
      : (Array.isArray(data) ? data : []);
    const findById = (data, id) => asArray(data).find((entry) =>
      String(entry?.id) === String(id)
    );

    setViewOrderData((previous) => ({
      ...previous,
      projectDetails:
        previous?.projectDetails ||
        viewItem?.projectDetails ||
        findById(projectData, viewItem?.projectId),
      distributorDetails:
        previous?.distributorDetails ||
        viewItem?.distributorDetails ||
        findById(distributorData, viewItem?.distributorId),
      countryDetails:
        previous?.countryDetails ||
        viewItem?.countryDetails ||
        findById(countryData, viewItem?.countryId),
      customerDetails:
        previous?.customerDetails ||
        viewItem?.customerDetails ||
        findById(customerData, viewItem?.customerId),
      currencyDetails:
        previous?.currencyDetails ||
        viewItem?.currencyDetails ||
        findById(currencyList, viewItem?.currencyId),
      assignedEngineerDetails:
        previous?.assignedEngineerDetails ||
        viewItem?.assignedEngineerDetails ||
        asArray(assignedEngineerList).find((engineer) =>
          String(engineer?.identityId ?? engineer?.id) === String(viewItem?.salesEngineerId)
        ),
    }));
  }, [
    viewItem,
    projectData,
    distributorData,
    countryData,
    customerData,
    currencyList,
    assignedEngineerList,
  ]);

  const navigateToQuotationGeneral = () => {
    navigate("/quotation-general", {
      state: {
        row: quoteGeneral?.row,
        id: quoteGeneral?.id,
      },
    });
  };

  useEffect(() => {
    const totalExworksPrice = Number(viewItem?.totalExworksPrice) || 0;
    const freightCharges = Number(viewItem?.freightCharges) || 0;
    const customDutyPercentage = Number(viewItem?.customDutyPercentage) || 0;
    const clearancePercentage = Number(viewItem?.clearanceChargesPercentage) || 0;
    const salesMarginPercentage = Number(viewItem?.salesMarginPercentage) || 0;
    const taxPercentage = Number(viewItem?.taxPercentage) || 0;
    const totalLandedCost = Number(viewItem?.totalLandedCost) || 0;
    const customDutyValue = (totalExworksPrice + freightCharges) * (customDutyPercentage / 100);
    const clearnceValue = (totalExworksPrice + freightCharges + customDutyValue) * (clearancePercentage / 100);
    const salesMarginFraction = Math.min(salesMarginPercentage / 100, 0.999999);
    const salesMarginValue = totalLandedCost > 0 ? (totalLandedCost / (1 - salesMarginFraction)) : 0;
    const taxValue = salesMarginValue * (taxPercentage / 100);

    setPercentageValue({
      customDutyValue: customDutyValue,
      clearnceValue: clearnceValue,
      taxValue: taxValue
    })
  }, [viewItem])

  useEffect(() => {
    setCurrencyDetail(currencyList?.find(item => item?.id === viewItem?.currencyId))
  }, [currencyList, viewItem?.currencyId])

  useEffect(() => {
    setTransportData(transportModeList?.find(item => item?.id === viewItem?.modeOfTransportId))
  }, [transportModeList, viewItem?.modeOfTransportId])

  useEffect(() => {
    setDeliveryTerms(deliveryTermsList?.find(item => item?.id === viewItem?.deliveryTermId))
  }, [deliveryTermsList, viewItem?.deliveryTermId])

  useEffect(() => {
    const leadTimeMatch = deliveryLeadTimeValues?.find(item => Number(item?.value) === Number(viewItem?.deliveryLeadTimeId));
    setDeliveryLeadTime(leadTimeMatch || null)
  }, [viewItem?.deliveryLeadTimeId])

  useEffect(() => {
    setPaymentTerms(paymentTermsList?.find(item => item?.id === viewItem?.paymentTermId))
  }, [paymentTermsList, viewItem?.paymentTermId])

  useEffect(() => {
    setBillingTo(customerData?.find(item => item?.id === viewItem?.billingCustomerId))
  }, [customerData, viewItem?.billingCustomerId])


  useEffect(() => {
    window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
      detail: {
        pathname: location.pathname,
        title: "View Factory Order",
        backTarget: { pathname: "/factory-order-list" },
      },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
        detail: {
          pathname: location.pathname,
          clear: true,
        },
      }));
    };
  }, [viewItem?.id, location.pathname]);

  return (
    <div className="container-fluid view-order-sec pl-3 pr-3">
      {/* view-sec start */}
      <section className="view-sec">
        <div className="row mt-2">
          <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <div className="card">
              <div className="card-header">
                <h1>General</h1>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td>
                          <p>Customer Name</p>
                          <h6>{viewOrderData?.customerDetails?.customerName || viewItem?.customerName || "-"}</h6>
                        </td>
                        <td>
                          <p>Order Reference</p>
                          <h6>{viewItem?.orderReference || "-"}</h6>
                        </td>
                        <td>
                          <p>Customer PO Number</p>
                          <h6>{viewItem?.purchaseOrderReference || "-"}</h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <p>PO Date</p>
                          <h6>{moment(viewItem?.purchaseOrderDate).format("DD-MM-YYYY")}</h6>
                        </td>
                        <td>
                          <p>Customer Address</p>
                          <h6>{viewItem?.customerAddress || "-"}</h6>
                        </td>
                        <td>
                          <p>Currency</p>
                          <h6>{viewOrderData?.currencyDetails?.shortCode || currencyDetail?.shortCode || viewItem?.currencyName || "-"}</h6>
                        </td>
                        <td>
                          <p>Conversion Rate</p>
                          <h6>{viewItem?.conversionRate || "-"}</h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <p>Phone Number</p>
                          <h6>{viewItem?.phone || "-"}</h6>
                        </td>
                        <td>
                          <p>Email Address</p>
                          <h6>{viewItem?.emailAddress || "-"}</h6>
                        </td>
                        <td>
                          <p>Country</p>
                          <h6>{viewOrderData?.countryDetails?.name || viewItem?.countryName || "-"}</h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <p>Distributor</p>
                          <h6>{viewOrderData?.distributorDetails?.distributorName || viewItem?.distributorName || "-"}</h6>
                        </td>
                        <td>
                          <p>Sales Engineer</p>
                          <h6>{[
                            viewOrderData?.assignedEngineerDetails?.firstName,
                            viewOrderData?.assignedEngineerDetails?.lastName,
                          ].filter(Boolean).join(" ") || viewItem?.salesEngineerName || "-"}</h6>
                        </td>
                        <td>
                          <p>Project Name</p>
                          <h6>{viewOrderData?.projectDetails?.projectName || viewItem?.projectName || "-"}</h6>
                        </td>
                        <td>
                          <p>Order Type</p>
                          <h6>{viewItem?.orderType === 1 ? "Job In Hand" : "Tender"} </h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <p>Bill To</p>
                          <h6>{billingTo?.customerName || "-"}</h6>
                        </td>
                        <td>
                          <p>Ship To</p>
                          <h6>{viewItem?.billingAddress || "-"}</h6>
                        </td>
                        <td>
                          <p>Delivery Contact Number</p>
                          <h6>{viewItem?.deliveryContactNo || "-"}</h6>
                        </td>
                        <td>
                          <p>Advance Payment</p>
                          <h6>
                            {viewItem?.advancePayment
                              ? `$${viewItem.advancePayment}`
                              : (viewItem?.paymentPercentage ? `${viewItem.paymentPercentage}%` : "-")}
                          </h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <p>Payment Terms</p>
                          <h6>{paymentTerm?.paymentDescription || "-"}</h6>
                        </td>
                        <td>
                          <p>Credit Days</p>
                          <h6>{viewItem?.creditDays || "-"}</h6>
                        </td>
                        <td>
                          <p>Delivery Lead Time</p>
                          <h6>{viewItem?.deliveryLeadTime || deliveryLeadTime?.label || "-"}</h6>
                        </td>
                        <td>
                          <p>Delivery Terms</p>
                          <h6>{deliveryTerms?.deliveryDescription || "-"}</h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <p>Mode of Transport</p>
                          <h6>{transportData?.transportationMode || "-"}</h6>
                        </td>
                       
                        <td>
                          <p>Request Delivery Date (From)</p>
                          <h6>{formatDate(viewItem?.deliveryFromDate, "view") || "-"}</h6>
                        </td>
                        <td>
                          <p>Request Delivery Date (To)</p>
                          <h6>{formatDate(viewItem?.deliveryToDate, "view") || "-"}</h6>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="table-responsive">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td>
                          <p>Other Details</p>
                          <h6>
                            {viewItem?.otherConditions || "No additional details provided."}
                          </h6>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="table-responsive">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td>
                          <p>Additional Instructions</p>
                          <h6>
                            {viewItem?.additionalInfo || "No additional instructions provided."}
                          </h6>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="card my-2">
              <ViewOrderSummary orderSummary={viewItem?.products} />
            </div>
            <div className="card mb-2 d-none">
              <ViewAccountSummary />
            </div>
            <div className="card mb-2">
              <ViewOrderCharges viewItem={viewItem} />
            </div>
            {viewItem?.attachments?.length > 0 ? (
              <div className="card document-card">
                <ViewDocuments attachments={viewItem.attachments} />
              </div>
            ) : null}
          </div>
        </div>
      </section>
      {/* view-sec end */}
    </div>
  );
};

export default ViewOrder;
